/* ============================================================
   IDEA MALA CLUB — imc.js (vanilla, módulo ES)
   Integra con Horizon: product-form-component, cart-drawer,
   cart-icon y los eventos estándar de carrito (@shopify/events).
   ============================================================ */

const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;
const money = (cents) => {
  const fmt = (window.Theme && window.Theme.imc && window.Theme.imc.moneyFormat) || '{{amount_with_comma_separator}} €';
  const amount = (cents / 100).toFixed(2);
  const withComma = amount.replace('.', ',');
  const withDot = amount;
  return fmt
    .replace('{{amount_with_comma_separator}}', withComma)
    .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100).toString())
    .replace('{{amount_no_decimals}}', Math.round(cents / 100).toString())
    .replace('{{amount}}', withDot);
};
const cookie = {
  get: (k) => document.cookie.split('; ').find((c) => c.startsWith(k + '='))?.split('=')[1],
  set: (k, v, hours) => {
    const d = new Date(Date.now() + hours * 3600 * 1000);
    document.cookie = `${k}=${v}; expires=${d.toUTCString()}; path=/; SameSite=Lax`;
  },
};

/* ---------- Toast ---------- */
const toast = (() => {
  let el;
  let t;
  return (text) => {
    if (!el) {
      el = document.createElement('div');
      el.className = 'imc-toast';
      el.setAttribute('role', 'status');
      document.body.appendChild(el);
    }
    el.textContent = text;
    el.classList.add('is-visible');
    clearTimeout(t);
    t = setTimeout(() => el.classList.remove('is-visible'), 2000);
  };
})();

/* ---------- Eventos de carrito (Horizon / standard events) ---------- */
const CART_EVENT = 'shopify:cart:lines-update';
let cartSnapshot = null;
async function fetchCart() {
  const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
  cartSnapshot = await r.json();
  document.dispatchEvent(new CustomEvent('imc:cart', { detail: cartSnapshot }));
  return cartSnapshot;
}
document.addEventListener(CART_EVENT, (e) => {
  const p = e.promise;
  if (p && p.then) {
    p.then(() => fetchCart()).catch(() => {});
  } else {
    fetchCart();
  }
  if (e.action === 'add' && !isDesktop()) {
    const t = (window.Theme && window.Theme.imc && window.Theme.imc.t.added) || 'Añadido al carrito ✓';
    if (p && p.then) p.then(({ detail }) => !detail?.didError && toast(t)).catch(() => {});
    else toast(t);
  }
});
// En móvil: toast en lugar de abrir el drawer automáticamente
function configureMobileCart() {
  const drawer = document.querySelector('cart-drawer-component');
  if (!drawer) return;
  if (!isDesktop()) drawer.removeAttribute('auto-open');
}
window.addEventListener('DOMContentLoaded', configureMobileCart);
window.matchMedia('(min-width: 990px)').addEventListener('change', configureMobileCart);

/* ---------- Reveal (entradas escalonadas) + contadores ---------- */
const revealIO = new IntersectionObserver(
  (entries) => {
    entries.forEach((en) => {
      if (en.isIntersecting) {
        en.target.classList.add('is-in');
        revealIO.unobserve(en.target);
      }
    });
  },
  { rootMargin: '0px 0px -10% 0px' }
);
function observeReveals(root = document) {
  root.querySelectorAll('.imc-reveal').forEach((el) => revealIO.observe(el));
}
observeReveals();
document.addEventListener('shopify:section:load', (e) => observeReveals(e.target));
// Fallback: Horizon puede re-hidratar secciones (los nodos observados se sustituyen) y el scroll vive en .page-wrapper
function revealByRect() {
  const vh = window.innerHeight;
  document.querySelectorAll('.imc-reveal:not(.is-in)').forEach((el) => {
    const r = el.getBoundingClientRect();
    if (r.top < vh * 0.95 && r.bottom > 0) el.classList.add('is-in');
  });
}
new MutationObserver(() => requestAnimationFrame(() => { observeReveals(); revealByRect(); })).observe(document.body, { childList: true, subtree: true });
[window, document.querySelector('.page-wrapper')].forEach((t) => t && t.addEventListener('scroll', () => requestAnimationFrame(revealByRect), { passive: true }));
window.addEventListener('load', revealByRect);
setInterval(revealByRect, 700);

class ImcCounter extends HTMLElement {
  connectedCallback() {
    this.target = parseFloat(this.dataset.target || '0');
    this.pfx = this.dataset.prefix || '';
    this.sfx = this.dataset.suffix || '';
    this.duration = parseInt(this.dataset.duration || '1200', 10);
    if (isNaN(this.target) || reduced()) {
      this.textContent = this.pfx + this.formatValue(this.target) + this.sfx;
      return;
    }
    this.textContent = this.pfx + '0' + this.sfx;
    this.done = false;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) this.start(io);
    });
    io.observe(this);
    this.timer = setInterval(() => {
      const r = this.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) this.start(io);
    }, 500);
  }
  start(io) {
    if (this.done) return;
    this.done = true;
    io?.disconnect();
    clearInterval(this.timer);
    this.run();
  }
  formatValue(v) {
    return Number.isInteger(this.target) ? Math.round(v).toLocaleString('es-ES') : v.toFixed(1).replace('.', ',');
  }
  run() {
    const start = performance.now();
    const step = (now) => {
      const p = Math.min(1, (now - start) / this.duration);
      const eased = 1 - Math.pow(1 - p, 3);
      this.textContent = this.pfx + this.formatValue(this.target * eased) + this.sfx;
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }
}
customElements.define('imc-counter', ImcCounter);

/* ---------- 5.1 Barra de anuncio ---------- */
class ImcAnnouncement extends HTMLElement {
  connectedCallback() {
    if (cookie.get('imc_announcement_closed') === '1' && !window.Shopify?.designMode) {
      this.hidden = true;
      return;
    }
    this.items = [...this.querySelectorAll('.imc-announcement__item')];
    this.index = 0;
    this.interval = parseInt(this.dataset.interval || '4000', 10);
    this.querySelector('.imc-announcement__close')?.addEventListener('click', () => {
      cookie.set('imc_announcement_closed', '1', 24);
      this.hidden = true;
      document.body.style.setProperty('--imc-announcement-h', '0px');
    });
    if (this.dataset.mode === 'marquee' || this.items.length < 2) {
      this.items.forEach((i) => i.classList.add('is-active'));
      return;
    }
    this.items[0].classList.add('is-active');
    if (reduced()) return;
    this.timer = setInterval(() => this.next(), this.interval);
  }
  next() {
    this.items[this.index].classList.remove('is-active');
    this.index = (this.index + 1) % this.items.length;
    this.items[this.index].classList.add('is-active');
  }
  disconnectedCallback() {
    clearInterval(this.timer);
  }
}
customElements.define('imc-announcement', ImcAnnouncement);

/* ---------- 5.2 Header ---------- */
class ImcHeader extends HTMLElement {
  connectedCallback() {
    this.header = this.querySelector('.imc-header');
    this.onScroll = () => {
      const y = window.scrollY || document.querySelector('.page-wrapper')?.scrollTop || 0;
      this.header.classList.toggle('is-scrolled', y > 24);
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    document.querySelector('.page-wrapper')?.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();

    // mega menús
    this.items = [...this.querySelectorAll('.imc-nav__item[data-mega]')];
    this.items.forEach((item) => {
      const btn = item.querySelector('.imc-nav__link');
      let leaveT;
      item.addEventListener('pointerenter', () => {
        clearTimeout(leaveT);
        this.open(item);
      });
      item.addEventListener('pointerleave', () => {
        leaveT = setTimeout(() => this.close(item), 120);
      });
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        item.classList.contains('is-open') ? this.close(item) : this.open(item);
      });
      item.addEventListener('keydown', (e) => e.key === 'Escape' && this.close(item, true));
    });
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) this.items.forEach((i) => this.close(i));
    });
    // drawer móvil
    this.querySelector('[data-open-drawer]')?.addEventListener('click', () => {
      this.querySelector('imc-drawer')?.open();
    });
  }
  open(item) {
    this.items.forEach((i) => i !== item && this.close(i));
    item.classList.add('is-open');
    item.querySelector('.imc-nav__link').setAttribute('aria-expanded', 'true');
  }
  close(item, focus) {
    item.classList.remove('is-open');
    const btn = item.querySelector('.imc-nav__link');
    btn.setAttribute('aria-expanded', 'false');
    if (focus) btn.focus();
  }
  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
  }
}
customElements.define('imc-header', ImcHeader);

class ImcDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.imc-drawer__panel');
    this.querySelectorAll('[data-close-drawer], .imc-drawer__overlay').forEach((b) =>
      b.addEventListener('click', () => this.close())
    );
    this.querySelectorAll('.imc-drawer__toggle').forEach((t) => {
      t.addEventListener('click', () => {
        const open = t.getAttribute('aria-expanded') === 'true';
        this.querySelectorAll('.imc-drawer__toggle').forEach((o) => {
          o.setAttribute('aria-expanded', 'false');
          o.nextElementSibling?.classList.remove('is-open');
        });
        if (!open) {
          t.setAttribute('aria-expanded', 'true');
          t.nextElementSibling?.classList.add('is-open');
        }
      });
    });
    this.onKey = (e) => e.key === 'Escape' && this.close();
    this.querySelectorAll('[data-open-target]').forEach((b) =>
      b.addEventListener('click', () => {
        this.close();
        const target = document.querySelector(b.dataset.openTarget);
        target?.showDialog?.();
      })
    );
  }
  open() {
    this.setAttribute('open', '');
    this.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    document.addEventListener('keydown', this.onKey);
    this.lastFocus = document.activeElement;
    setTimeout(() => this.querySelector('[data-close-drawer]')?.focus(), 320);
  }
  close() {
    this.removeAttribute('open');
    this.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    document.removeEventListener('keydown', this.onKey);
    this.lastFocus?.focus?.();
  }
}
customElements.define('imc-drawer', ImcDrawer);

/* ---------- 5.5 Carrusel con contador "1 / 6" ---------- */
class ImcCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.imc-carousel__track');
    this.slides = [...this.querySelectorAll('.imc-carousel__slide')];
    this.prev = this.querySelector('.imc-carousel__arrow--prev');
    this.next = this.querySelector('.imc-carousel__arrow--next');
    this.counter = this.querySelector('[data-counter]');
    this.total = this.slides.length;
    this.index = 0;
    this.prev?.addEventListener('click', () => this.go(-1));
    this.next?.addEventListener('click', () => this.go(1));
    let raf;
    this.track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.sync());
      },
      { passive: true }
    );
    this.track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') this.go(1);
      if (e.key === 'ArrowLeft') this.go(-1);
    });
    this.sync();
  }
  slideWidth() {
    const s = this.slides[0];
    if (!s) return 1;
    const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
    return s.getBoundingClientRect().width + gap;
  }
  go(dir) {
    const w = this.slideWidth();
    this.track.scrollBy({ left: dir * w, behavior: reduced() ? 'auto' : 'smooth' });
  }
  sync() {
    const w = this.slideWidth();
    this.index = Math.min(this.total - 1, Math.max(0, Math.round(this.track.scrollLeft / w)));
    if (this.counter) this.counter.textContent = `${this.index + 1} / ${this.total}`;
    const maxScroll = this.track.scrollWidth - this.track.clientWidth - 2;
    if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
    if (this.next) this.next.disabled = this.track.scrollLeft >= maxScroll;
  }
}
customElements.define('imc-carousel', ImcCarousel);

/* ---------- Tarjeta de producto: hover-swap, flip móvil, añadir rápido ---------- */
class ImcProductCard extends HTMLElement {
  connectedCallback() {
    const main = this.querySelector('.imc-card__img--main');
    const hover = this.querySelector('.imc-card__img--hover');
    const done = () => this.classList.add('is-loaded');
    if (main) {
      if (main.complete) done();
      else main.addEventListener('load', done, { once: true });
    }
    // precarga de la segunda imagen (no parpadea)
    if (hover) {
      const pre = () => {
        if (hover.dataset.src) {
          hover.src = hover.dataset.src;
          delete hover.dataset.src;
        }
      };
      this.addEventListener('pointerenter', pre, { once: true });
      const io = new IntersectionObserver((en) => {
        if (en[0].isIntersecting) {
          pre();
          io.disconnect();
        }
      }, { rootMargin: '200px' });
      io.observe(this);
    }
    this.querySelector('.imc-card__flip')?.addEventListener('click', (e) => {
      e.preventDefault();
      this.classList.toggle('is-flipped');
    });
    // quick add
    this.openBtn = this.querySelector('.imc-card__quick-btn--open');
    this.openBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      if (this.dataset.singleVariant === 'true') {
        this.submit(this.dataset.variantId);
        return;
      }
      this.classList.add('is-choosing');
    });
    this.querySelector('[data-close-sizes]')?.addEventListener('click', () => this.classList.remove('is-choosing'));
    this.querySelectorAll('.imc-card__sizes .imc-size-btn').forEach((b) =>
      b.addEventListener('click', (e) => {
        e.preventDefault();
        if (b.disabled) return;
        this.querySelectorAll('.imc-size-btn').forEach((o) => o.classList.remove('is-selected'));
        b.classList.add('is-selected');
        this.submit(b.dataset.variantId);
      })
    );
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) this.classList.remove('is-choosing');
    });
  }
  submit(variantId) {
    const form = this.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) return;
    form.querySelector('input[name="id"]').value = variantId;
    form.querySelector('input[name="id"]').disabled = false;
    const btn = form.querySelector('button[type="submit"]');
    btn?.click();
    setTimeout(() => this.classList.remove('is-choosing'), 400);
  }
}
customElements.define('imc-product-card', ImcProductCard);

/* ---------- 5.12 Testimonios autoplay ---------- */
class ImcTestimonials extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.imc-testimonials__track');
    this.items = [...this.querySelectorAll('.imc-testimonial')];
    this.dots = this.querySelector('.imc-testimonials__dots');
    this.index = 0;
    this.build();
    this.addEventListener('pointerenter', () => this.stop());
    this.addEventListener('pointerleave', () => this.start());
    window.addEventListener('resize', () => this.update());
    this.start();
    // swipe simple
    let x0 = null;
    this.track.addEventListener('pointerdown', (e) => (x0 = e.clientX));
    this.track.addEventListener('pointerup', (e) => {
      if (x0 === null) return;
      const dx = e.clientX - x0;
      if (Math.abs(dx) > 40) this.goTo(this.index + (dx < 0 ? 1 : -1));
      x0 = null;
    });
  }
  perView() {
    return isDesktop() ? 3 : 1;
  }
  pages() {
    return Math.max(1, Math.ceil(this.items.length / this.perView()));
  }
  build() {
    if (!this.dots) return;
    this.dots.innerHTML = '';
    for (let i = 0; i < this.pages(); i++) {
      const b = document.createElement('button');
      b.type = 'button';
      b.setAttribute('aria-label', `Ir a la opinión ${i + 1}`);
      b.addEventListener('click', () => this.goTo(i));
      this.dots.appendChild(b);
    }
    this.update();
  }
  goTo(i) {
    const p = this.pages();
    this.index = ((i % p) + p) % p;
    this.update();
  }
  update() {
    const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
    const w = this.items[0]?.getBoundingClientRect().width || 0;
    const per = this.perView();
    this.track.style.transform = `translateX(-${this.index * per * (w + gap)}px)`;
    this.dots?.querySelectorAll('button').forEach((b, i) => b.classList.toggle('is-active', i === this.index));
  }
  start() {
    if (reduced() || this.pages() < 2) return;
    this.stop();
    this.timer = setInterval(() => this.goTo(this.index + 1), parseInt(this.dataset.interval || '5000', 10));
  }
  stop() {
    clearInterval(this.timer);
  }
  disconnectedCallback() {
    this.stop();
  }
}
customElements.define('imc-testimonials', ImcTestimonials);

/* ---------- Acordeón (FAQ + PDP): uno abierto, altura animada ---------- */
class ImcAccordion extends HTMLElement {
  connectedCallback() {
    this.buttons = [...this.querySelectorAll('[data-acc-btn]')];
    this.buttons.forEach((b) => {
      const panel = document.getElementById(b.getAttribute('aria-controls'));
      if (b.getAttribute('aria-expanded') === 'true') this.setOpen(b, panel, true, true);
      b.addEventListener('click', () => {
        const open = b.getAttribute('aria-expanded') === 'true';
        if (this.dataset.single !== 'false') {
          this.buttons.forEach((o) => {
            if (o !== b) this.setOpen(o, document.getElementById(o.getAttribute('aria-controls')), false);
          });
        }
        this.setOpen(b, panel, !open);
      });
    });
  }
  setOpen(btn, panel, open, instant) {
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (!panel) return;
    panel.classList.toggle('is-open', open);
    if (open) {
      panel.style.maxHeight = panel.scrollHeight + 'px';
      if (!instant) panel.addEventListener('transitionend', () => open && (panel.style.maxHeight = 'none'), { once: true });
      else panel.style.maxHeight = 'none';
    } else {
      panel.style.maxHeight = panel.scrollHeight + 'px';
      requestAnimationFrame(() => (panel.style.maxHeight = '0px'));
    }
  }
}
customElements.define('imc-accordion', ImcAccordion);

/* ---------- 5.14 Email con código + pop-up de salida ---------- */
class ImcEmailCapture extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[type="email"]');
    this.error = this.querySelector('.imc-email__error');
    this.code = this.querySelector('.imc-code');
    this.form?.addEventListener('submit', (e) => this.submit(e));
    this.code?.addEventListener('click', () => {
      navigator.clipboard?.writeText(this.code.textContent.trim());
      toast('Código copiado');
    });
  }
  valid(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);
  }
  async submit(e) {
    e.preventDefault();
    const v = this.input.value.trim();
    if (!this.valid(v)) {
      this.error.textContent = this.dataset.errorText || 'Eso no parece un email. Inténtalo otra vez.';
      this.input.setAttribute('aria-invalid', 'true');
      this.input.focus();
      return;
    }
    this.error.textContent = '';
    const btn = this.form.querySelector('button');
    btn.dataset.loading = 'true';
    try {
      await fetch(this.form.action, { method: 'POST', body: new FormData(this.form), credentials: 'same-origin' });
    } catch (_) {
      /* la suscripción se reintenta con el envío nativo si fetch falla */
    }
    btn.dataset.loading = 'false';
    this.classList.add('is-success');
    sessionStorage.setItem('imc_email_done', '1');
    localStorage.setItem('imc_email_done', '1');
    this.dispatchEvent(new CustomEvent('imc:subscribed', { bubbles: true }));
  }
}
customElements.define('imc-email-capture', ImcEmailCapture);

class ImcExitPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;
    const tpl = (window.Theme && window.Theme.template && window.Theme.template.name) || '';
    const blocked = /product|cart|checkout/.test(tpl) || /\/checkouts?\//.test(location.pathname);
    if (blocked || sessionStorage.getItem('imc_popup_shown') || localStorage.getItem('imc_email_done')) return;
    if (window.Shopify?.designMode && this.dataset.preview !== 'true') return;
    this.querySelector('.imc-popup__close')?.addEventListener('click', () => this.dialog.close());
    this.dialog.addEventListener('click', (e) => e.target === this.dialog && this.dialog.close());
    const delay = parseInt(this.dataset.delay || '25000', 10);
    const show = () => {
      if (sessionStorage.getItem('imc_popup_shown')) return;
      sessionStorage.setItem('imc_popup_shown', '1');
      this.dialog.showModal();
    };
    if (isDesktop()) {
      document.addEventListener('mouseleave', (e) => e.clientY <= 0 && show());
      setTimeout(show, delay * 2.4); // respaldo desktop: 60 s
    } else {
      setTimeout(show, delay);
      const onScroll = () => {
        const doc = document.documentElement;
        if (window.scrollY / (doc.scrollHeight - innerHeight) > 0.5) {
          show();
          window.removeEventListener('scroll', onScroll);
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
    }
  }
}
customElements.define('imc-exit-popup', ImcExitPopup);

document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-open-popup]');
  if (!btn) return;
  const dlg = document.querySelector('imc-exit-popup dialog');
  if (dlg && !dlg.open) dlg.showModal();
});

/* ---------- 5.16 Barra inferior sticky (móvil) ---------- */
class ImcStickyBar extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('.imc-sticky-bar');
    document.body.classList.add('imc-has-sticky-bar');
    const threshold = parseInt(this.dataset.after || '400', 10);
    const onScroll = () => this.bar.classList.toggle('is-visible', window.scrollY > threshold);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }
  disconnectedCallback() {
    document.body.classList.remove('imc-has-sticky-bar');
  }
}
customElements.define('imc-sticky-bar', ImcStickyBar);

/* ---------- Barra de envío gratis (PDP + drawer) ---------- */
class ImcShipbar extends HTMLElement {
  connectedCallback() {
    this.threshold = parseInt(this.dataset.threshold || '4500', 10);
    this.onCart = (e) => this.render(e.detail);
    document.addEventListener('imc:cart', this.onCart);
    // Horizon hace morph del drawer y sustituye los hijos: re-pintamos con el último snapshot
    this.mo = new MutationObserver(() => {
      if (!this.painting) this.render(cartSnapshot);
    });
    this.mo.observe(this, { childList: true, subtree: true, characterData: true });
    if (cartSnapshot) this.render(cartSnapshot);
    else fetchCart();
  }
  disconnectedCallback() {
    document.removeEventListener('imc:cart', this.onCart);
    this.mo?.disconnect();
  }
  render(cart) {
    if (!cart) return;
    const fill = this.querySelector('.imc-shipbar__fill');
    const text = this.querySelector('.imc-shipbar__text');
    if (!fill || !text) return;
    const total = cart.total_price; // total con descuentos automáticos aplicados
    const missing = Math.max(0, this.threshold - total);
    const pct = Math.min(100, (total / this.threshold) * 100);
    const done = this.dataset.textDone || '¡Envío gratis conseguido!';
    const tpl = this.dataset.text || 'Te faltan <strong>{amount}</strong> para el envío gratis';
    const html = missing === 0 ? `<strong>${done}</strong>` : tpl.replace('{amount}', money(missing));
    this.painting = true;
    fill.style.setProperty('--progress', pct + '%');
    this.classList.toggle('is-complete', missing === 0);
    if (text.innerHTML !== html) text.innerHTML = html;
    requestAnimationFrame(() => (this.painting = false));
  }
}
customElements.define('imc-shipbar', ImcShipbar);

/* ---------- 6. PDP ---------- */
class ImcProduct extends HTMLElement {
  connectedCallback() {
    try {
      this.product = JSON.parse(this.querySelector('[data-product-json]').textContent);
    } catch (_) {
      return;
    }
    this.form = this.querySelector('form[data-type="add-to-cart-form"]');
    this.idInput = this.form?.querySelector('input[name="id"]');
    this.priceEl = this.querySelector('[data-price]');
    this.compareEl = this.querySelector('[data-compare]');
    this.lowestEl = this.querySelector('[data-lowest]');
    this.urgency = this.querySelector('.imc-urgency');
    this.notify = this.querySelector('.imc-notify');
    this.atc = this.querySelector('[data-atc]');
    this.selected = {};
    this.product.options.forEach((o, i) => {
      const sel = this.querySelector(`.imc-option[data-index="${i}"] .is-selected`);
      this.selected[i] = sel?.dataset.value || o.values[0];
    });
    this.querySelectorAll('.imc-option [data-value]').forEach((btn) =>
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.closest('.imc-option').dataset.index, 10);
        this.selected[idx] = btn.dataset.value;
        btn.closest('.imc-option').querySelectorAll('[data-value]').forEach((o) => o.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        const label = btn.closest('.imc-option').querySelector('[data-selected-label]');
        if (label) label.textContent = btn.dataset.label || btn.dataset.value;
        this.update();
        if (btn.closest('.imc-option').dataset.type === 'color') this.switchGallery(btn.dataset.value);
      })
    );
    // guía de tallas
    const sizeDialog = this.querySelector('.imc-size-dialog');
    this.querySelectorAll('[data-open-size-guide]').forEach((b) => b.addEventListener('click', () => sizeDialog?.showModal()));
    sizeDialog?.querySelector('[data-close]')?.addEventListener('click', () => sizeDialog.close());
    sizeDialog?.addEventListener('click', (e) => e.target === sizeDialog && sizeDialog.close());
    sizeDialog?.querySelectorAll('.imc-unit-toggle button').forEach((b) =>
      b.addEventListener('click', () => {
        sizeDialog.querySelectorAll('.imc-unit-toggle button').forEach((o) => o.classList.toggle('is-active', o === b));
        const inch = b.dataset.unit === 'in';
        sizeDialog.querySelectorAll('[data-cm]').forEach((td) => (td.textContent = inch ? (td.dataset.cm / 2.54).toFixed(1) : td.dataset.cm));
      })
    );
    // galería
    this.initGallery();
    // sticky ATC
    this.initSticky();
    this.update();
  }
  variant() {
    return this.product.variants.find((v) => this.product.options.every((_, i) => v.options[i] === this.selected[i]));
  }
  update() {
    const v = this.variant();
    // marcar tallas agotadas para el color seleccionado
    const sizeOpt = this.querySelector('.imc-option[data-type="size"]');
    if (sizeOpt) {
      const sIdx = parseInt(sizeOpt.dataset.index, 10);
      sizeOpt.querySelectorAll('[data-value]').forEach((b) => {
        const candidate = this.product.variants.find((x) =>
          this.product.options.every((_, i) => (i === sIdx ? x.options[i] === b.dataset.value : x.options[i] === this.selected[i]))
        );
        const soldout = !candidate || !candidate.available;
        b.classList.toggle('is-soldout', soldout);
        b.setAttribute('aria-label', `${b.dataset.value}${soldout ? ' (agotada)' : ''}`);
      });
    }
    if (!v) {
      this.atc && (this.atc.disabled = true);
      return;
    }
    this.idInput.value = v.id;
    this.idInput.disabled = false;
    if (this.priceEl) this.priceEl.textContent = money(v.price);
    if (this.compareEl) {
      const onSale = v.compare_at_price && v.compare_at_price > v.price;
      this.compareEl.hidden = !onSale;
      this.compareEl.textContent = onSale ? money(v.compare_at_price) : '';
      if (this.lowestEl) this.lowestEl.hidden = !onSale;
    }
    const soldout = !v.available;
    if (this.atc) {
      this.atc.disabled = soldout;
      this.atc.querySelector('[data-atc-text]').textContent = soldout
        ? this.dataset.soldoutText || 'Agotada'
        : this.dataset.atcText || 'Añadir al carrito';
    }
    this.notify?.classList.toggle('is-visible', soldout);
    if (this.notify && soldout) {
      const hidden = this.notify.querySelector('input[name="contact[variant]"]');
      if (hidden) hidden.value = `${this.product.title} — ${v.title}`;
    }
    // urgencia veraz: solo si stock real < 10 (inventory_quantity viene del JSON del tema)
    if (this.urgency) {
      const q = typeof v.inventory_quantity === 'number' ? v.inventory_quantity : null;
      const managed = v.inventory_management === 'shopify';
      const show = managed && q !== null && q > 0 && q < 10;
      this.urgency.classList.toggle('is-visible', show);
      if (show) this.urgency.textContent = (this.dataset.urgencyText || 'Quedan {qty} unidades en talla {size}')
        .replace('{qty}', q)
        .replace('{size}', this.selected[parseInt(sizeOpt?.dataset.index ?? '1', 10)] || v.title);
    }
    // sticky
    const bp = this.querySelector('[data-atc-price]');
    if (bp) bp.textContent = money(v.price);
    const sp = this.querySelector('[data-sticky-price]');
    if (sp) sp.textContent = money(v.price);
    // packs "compra más, ahorra más": recalculados con el precio de la variante
    const pct = (window.Theme && window.Theme.imc && window.Theme.imc.secondPct) || 20;
    this.querySelectorAll('[data-pack]').forEach((el) => {
      const q = parseInt(el.dataset.qty || '2', 10);
      const u = parseInt(el.dataset.units || '1', 10);
      const was = v.price * q;
      const disc = Math.round((v.price * pct) / 100) * u;
      const set = (sel, val) => { const n = el.querySelector(sel); if (n) n.textContent = money(val); };
      set('[data-pack-was]', was);
      set('[data-pack-now]', was - disc);
      set('[data-pack-save]', disc);
    });
    const sb = this.querySelector('[data-sticky-atc]');
    if (sb) sb.disabled = soldout;
    // URL
    const url = new URL(location.href);
    url.searchParams.set('variant', v.id);
    history.replaceState({}, '', url);
    this.dispatchEvent(new CustomEvent('imc:variant', { detail: v, bubbles: true }));
  }
  /* galería: scroll snap + dots + thumbs + crossfade por color + zoom */
  initGallery() {
    this.gallery = this.querySelector('.imc-gallery');
    if (!this.gallery) return;
    this.track = this.gallery.querySelector('.imc-gallery__track');
    this.slides = [...this.gallery.querySelectorAll('.imc-gallery__slide')];
    this.dots = this.gallery.querySelector('.imc-gallery__dots');
    this.thumbs = [...this.gallery.querySelectorAll('.imc-gallery__thumb')];
    this.buildDots();
    this.track.addEventListener('scroll', () => requestAnimationFrame(() => this.syncGallery()), { passive: true });
    this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.goSlide(i)));
    const zoom = this.querySelector('.imc-zoom-dialog');
    this.gallery.querySelectorAll('.imc-gallery__zoom, .imc-gallery__slide img').forEach((el) =>
      el.addEventListener('click', () => {
        if (!zoom) return;
        const slide = el.closest('.imc-gallery__slide');
        const src = slide?.querySelector('img')?.dataset.zoom;
        if (!src) return;
        zoom.querySelector('img').src = src;
        zoom.showModal();
      })
    );
    zoom?.addEventListener('click', () => zoom.close());
    const initialColor = this.selected[this.colorIndex()];
    if (initialColor) this.switchGallery(initialColor, true);
  }
  colorIndex() {
    const opt = this.querySelector('.imc-option[data-type="color"]');
    return opt ? parseInt(opt.dataset.index, 10) : -1;
  }
  visibleSlides() {
    return this.slides.filter((s) => !s.hidden);
  }
  buildDots() {
    if (!this.dots) return;
    this.dots.innerHTML = '';
    this.visibleSlides().forEach((_, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.setAttribute('aria-label', `Imagen ${i + 1}`);
      b.addEventListener('click', () => this.goSlide(i));
      this.dots.appendChild(b);
    });
    this.syncGallery();
  }
  goSlide(i) {
    const s = this.visibleSlides()[i];
    if (!s) return;
    this.track.scrollTo({ left: s.offsetLeft, behavior: reduced() ? 'auto' : 'smooth' });
  }
  syncGallery() {
    const vis = this.visibleSlides();
    const w = vis[0]?.getBoundingClientRect().width || 1;
    const i = Math.round(this.track.scrollLeft / w);
    this.dots?.querySelectorAll('button').forEach((b, k) => b.classList.toggle('is-active', k === i));
    const visThumbs = this.thumbs.filter((t) => !t.hidden);
    visThumbs.forEach((t, k) => t.classList.toggle('is-active', k === i));
  }
  switchGallery(color, instant) {
    const key = (color || '').toLowerCase();
    const matches = this.slides.filter((s) => (s.dataset.color || '').toLowerCase() === key);
    const apply = () => {
      this.slides.forEach((s, i) => {
        const show = matches.length ? matches.includes(s) : true;
        s.hidden = !show;
        if (this.thumbs[i]) this.thumbs[i].hidden = !show;
      });
      this.track.scrollTo({ left: 0, behavior: 'auto' });
      this.buildDots();
      // imagen del sticky
      const first = this.visibleSlides()[0]?.querySelector('img');
      const sticky = this.querySelector('.imc-sticky-atc img');
      if (first && sticky) sticky.src = first.currentSrc || first.src;
    };
    if (instant || reduced()) return apply();
    this.gallery.classList.add('is-switching');
    setTimeout(() => {
      apply();
      this.gallery.classList.remove('is-switching');
    }, 250);
  }
  initSticky() {
    const bar = this.querySelector('.imc-sticky-atc');
    const main = this.querySelector('[data-atc]');
    if (!bar || !main) return;
    const io = new IntersectionObserver((en) => bar.classList.toggle('is-visible', !en[0].isIntersecting && en[0].boundingClientRect.top < 0));
    io.observe(main);
    bar.querySelector('[data-sticky-atc]')?.addEventListener('click', () => {
      // si la talla no está elegida, llevamos al selector; si sí, añadimos
      const sizeOpt = this.querySelector('.imc-option[data-type="size"]');
      if (sizeOpt && !sizeOpt.querySelector('.is-selected')) {
        sizeOpt.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      main.click();
    });
  }
}
customElements.define('imc-product', ImcProduct);

/* ---------- 6.11 La 2ª al -20 % ---------- */
class ImcSecondTee extends HTMLElement {
  connectedCallback() {
    const btn = this.querySelector('[data-add-second]');
    const form = this.querySelector('form[data-type="add-to-cart-form"]');
    const sizeSel = this.querySelector('select[data-second-size]');
    btn?.addEventListener('click', (e) => {
      e.preventDefault();
      if (!form) return;
      if (sizeSel) form.querySelector('input[name="id"]').value = sizeSel.value;
      form.querySelector('button[type="submit"]')?.click();
    });
    // sincroniza la talla con la elegida en la PDP
    document.addEventListener('imc:variant', (e) => {
      if (!sizeSel) return;
      const size = e.detail?.options?.[1] || e.detail?.option2;
      const opt = [...sizeSel.options].find((o) => o.dataset.size === size && !o.disabled);
      if (opt) sizeSel.value = opt.value;
    });
  }
}
customElements.define('imc-second-tee', ImcSecondTee);

/* ---------- 7. Colección: filtros, orden, cargar más ---------- */
class ImcCollection extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.grid = this.querySelector('.imc-grid');
    this.forms = [...this.querySelectorAll('form[data-filters]')];
    this.forms.forEach((f) => {
      f.addEventListener('change', (e) => {
        if (e.target.matches('input[type="checkbox"], select')) this.apply(f);
      });
      f.addEventListener('submit', (e) => {
        e.preventDefault();
        this.apply(f);
      });
      f.querySelectorAll('input[type="number"]').forEach((i) => i.addEventListener('change', () => this.apply(f)));
    });
    this.querySelector('.imc-coll__sort')?.addEventListener('change', (e) => {
      const url = new URL(location.href);
      url.searchParams.set('sort_by', e.target.value);
      url.searchParams.delete('page');
      this.load(url, true);
    });
    this.bind();
    this.querySelector('.imc-coll__filter-btn')?.addEventListener('click', () => this.querySelector('imc-drawer')?.open());
    window.addEventListener('popstate', () => this.load(new URL(location.href), true, false));
  }
  bind() {
    this.querySelector('[data-load-more]')?.addEventListener('click', (e) => {
      e.preventDefault();
      this.load(new URL(e.currentTarget.href, location.origin), false);
    });
    this.querySelectorAll('.imc-filters__active a').forEach((a) =>
      a.addEventListener('click', (e) => {
        e.preventDefault();
        this.load(new URL(a.href, location.origin), true);
      })
    );
  }
  apply(form) {
    const url = new URL(location.href);
    [...url.searchParams.keys()].forEach((k) => (k.startsWith('filter.') || k === 'page') && url.searchParams.delete(k));
    new FormData(form).forEach((v, k) => v !== '' && url.searchParams.append(k, v));
    this.load(url, true);
  }
  async load(url, replace, push = true) {
    this.grid.classList.add('is-loading');
    const fetchUrl = new URL(url);
    fetchUrl.searchParams.set('section_id', this.sectionId);
    const html = await fetch(fetchUrl).then((r) => r.text());
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('imc-collection');
    if (!fresh) return;
    if (replace) {
      this.querySelector('.imc-coll__layout').replaceWith(fresh.querySelector('.imc-coll__layout'));
      this.querySelector('.imc-coll__count')?.replaceWith(fresh.querySelector('.imc-coll__count'));
      this.grid = this.querySelector('.imc-grid');
      this.forms = [...this.querySelectorAll('form[data-filters]')];
      this.forms.forEach((f) => f.addEventListener('change', (e) => e.target.matches('input, select') && this.apply(f)));
      if (push) history.pushState({}, '', url);
      this.querySelector('.imc-coll__bar')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      fresh.querySelectorAll('.imc-grid > *').forEach((li) => this.grid.appendChild(li));
      const more = this.querySelector('.imc-coll__more');
      const freshMore = fresh.querySelector('.imc-coll__more');
      if (more) freshMore ? more.replaceWith(freshMore) : more.remove();
      if (push) history.replaceState({}, '', url);
    }
    this.grid.classList.remove('is-loading');
    this.bind();
    observeReveals(this);
  }
}
customElements.define('imc-collection', ImcCollection);

/* ---------- UGC / TikTok modal ---------- */
class ImcUgc extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.querySelectorAll('.imc-ugc__item').forEach((b) =>
      b.addEventListener('click', () => {
        const url = b.dataset.video;
        const frame = this.dialog.querySelector('.imc-modal__frame');
        if (/tiktok\.com/.test(url)) {
          const id = url.match(/video\/(\d+)/)?.[1];
          frame.innerHTML = id
            ? `<iframe src="https://www.tiktok.com/embed/v2/${id}" allow="autoplay; encrypted-media" loading="lazy" title="Vídeo de cliente"></iframe>`
            : `<a href="${url}" target="_blank" rel="noopener" style="color:#fff;display:grid;place-items:center;height:100%">Ver en TikTok</a>`;
        } else {
          frame.innerHTML = `<video src="${url}" controls autoplay playsinline></video>`;
        }
        this.dialog.showModal();
      })
    );
    this.dialog?.querySelector('.imc-modal__close')?.addEventListener('click', () => this.dialog.close());
    this.dialog?.addEventListener('close', () => (this.dialog.querySelector('.imc-modal__frame').innerHTML = ''));
    this.dialog?.addEventListener('click', (e) => e.target === this.dialog && this.dialog.close());
  }
}
customElements.define('imc-ugc', ImcUgc);

/* ---------- Hero: autoplay robusto. Si iOS (ahorro de energía) bloquea el vídeo
   y pinta el botón de play, lo sustituimos por el poster: nunca un play visible. ---------- */
document.querySelectorAll('.imc-hero video').forEach((v) => {
  v.muted = true;
  v.setAttribute('muted', '');
  v.playsInline = true;
  let played = false;
  v.addEventListener('playing', () => (played = true), { once: true });
  const tryPlay = () => v.play().catch(() => {});
  const io = new IntersectionObserver((en) => (en[0].isIntersecting ? tryPlay() : v.pause()));
  io.observe(v);
  const fallback = () => {
    if (played || !v.isConnected) return;
    const poster = v.getAttribute('poster');
    if (!poster) return;
    const img = document.createElement('img');
    img.src = poster;
    img.alt = '';
    img.className = 'imc-hero__fallback';
    io.disconnect();
    v.replaceWith(img);
  };
  setTimeout(tryPlay, 400);
  setTimeout(fallback, 2200);
});

export { toast, fetchCart, money };

/* ---------- 6.13 Vistas recientemente (IDs de Horizon en localStorage) ---------- */
(function recentlyViewed() {
  const host = document.querySelector('[data-recently-viewed]');
  if (!host) return;
  let ids = [];
  try {
    ids = JSON.parse(localStorage.getItem('viewedProducts') || '[]');
  } catch (_) {
    return;
  }
  ids = ids.filter((id) => String(id) !== String(host.dataset.exclude)).slice(0, 6);
  if (!ids.length) return;
  const q = ids.map((id) => `id:${id}`).join(' OR ');
  const url = `${(window.Theme && window.Theme.routes && window.Theme.routes.search_url) || '/search'}?type=product&q=${encodeURIComponent(q)}&section_id=${host.dataset.sectionId}`;
  fetch(url)
    .then((r) => r.text())
    .then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const el = doc.querySelector('[data-recently-viewed-results]');
      if (el) host.replaceWith(el);
    })
    .catch(() => {});
})();
